import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import FormField, {Props} from '@src/components/Form/FormField';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import ControlledList from '@src/components/ControlledList/ControlledList';
import {ListConfigFieldTypes, ListConfigNamedColumn} from '@src/@types/components/List';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import {DeadlineAtField} from './DeadlineAtField';
import {ProductField} from './ProductField';
import {TotalPriceField} from './TotalPriceField';
import {QuantityField} from './QuantityField';
import {TableCaption} from './TableCaption';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import CellBeforeTableRestColumns from './CellBeforeTableRestColumns';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import {IdField} from './IdField';
import classnames from '@src/helpers/classnames';
import {ActualProductPriceField} from './ActualProductPriceField';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {IndicatorsField} from './IndicatorsField';
import ExtraRowTabs from './extra_row/ExtraRowTabs';
import usePermissions from '@src/hooks/usePermissions';
import {IFormFieldDesignContext} from '@src/@interfaces/contexts/IFormFieldDesignContext';
import useFormFieldDesignContextProviderValue from '@src/hooks/useFormFieldDesignContextProviderValue';
import {tap} from '@src/helpers/tap';

const columns = [
    {
        name: 'id',
        valueFrom: 'number_to_display',
        label: '#',
        type: ListConfigFieldTypes.NUMBER,
        forceVisible: true,
    },
    {
        name: 'indicators',
        label: 'OrderForm.products.fields.indicators',
        type: ListConfigFieldTypes.CUSTOM,
    },
    {
        name: 'product_id',
        label: 'OrderForm.products.fields.product',
        type: ListConfigFieldTypes.CUSTOM
    },
    {
        name: 'price',
        label: 'OrderForm.products.fields.price',
        type: ListConfigFieldTypes.CUSTOM
    },
    {
        name: 'quantity',
        label: 'OrderForm.products.fields.quantity',
        type: ListConfigFieldTypes.CUSTOM
    },
    {
        name: 'total_price',
        label: 'OrderForm.products.fields.total_price',
        type: ListConfigFieldTypes.CUSTOM
    },
    {
        name: 'deadline_at',
        label: 'OrderForm.products.fields.deadline_at',
        type: ListConfigFieldTypes.CUSTOM
    },
];

export const columnsTDProps: Record<typeof columns[number]['name'], React.HTMLAttributes<HTMLTableDataCellElement>> = {
    id: {style: {width: '20px'}},
    status_id: {style: {minWidth: '100px'}},
    product_id: {style: {minWidth: '150px', maxWidth: '400px'}},
    product_price: {style: {width: '80px'}},
    department_id: {style: {minWidth: '100px'}},
    price: {style: {width: '85px'}},
    total_price: {style: {width: '100px'}},
    quantity: {style: {width: '100px'}},
    custom_price: {style: {width: '130px'}},
    deadline_at: {style: {width: '170px'}},
};

function getCustomColumnComponent(config: ListConfigNamedColumn) {
    switch (config.name) {
        case 'id':
            return null;

        case 'product_id':
            return ProductField;

        case 'indicators':
            return IndicatorsField;

        case 'price':
            return ActualProductPriceField;

        case 'quantity':
            return QuantityField;

        case 'total_price':
            return TotalPriceField;

        case 'deadline_at':
            return DeadlineAtField;
    }

    return null;
}

const listContextValues = {getCustomColumnComponent};

function FormFieldDesignContextProvider(props: {
    children: React.ReactNode,
    contextValue?: Partial<IFormFieldDesignContext>,
}) {
    const contextValue = useFormFieldDesignContextProviderValue({
        contextValue: props.contextValue,
    });

    return <FormFieldDesignContext.Provider value={contextValue}>{props.children}</FormFieldDesignContext.Provider>;
}

function ActualForm(props: Props<IOrderModel>) {
    const tabsContext = useContext(FormTabsContext);
    const [designContext] = useContext(FormFieldDesignContext);
    const {control, setValue} = useFormContext<IOrderModel>();
    const {cant} = usePermissions();
    const {fields, append, remove} = useFieldArray({
        control,
        name: props.fieldName as 'products',
        keyName: 'autoGeneratedUuid',
    });
    const [collapsedRows, setCollapsedRows] = useState<Record<string | number, boolean>>({});
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => void setValue('products_is_empty', fields.length <= 0 ? 1 : null), [fields.length, setValue]);

    useEffect(() => {
        if (!props.model?.products) {
            return;
        }

        const newMap = props.model.products.reduce<Record<string | number, boolean>>((res, item) => {
            res[item.id] = true;

            return res;
        }, {});

        setCollapsedRows(map => ({...map, ...newMap}));
    }, [props.model?.products]);

    useEffect(() => {
        const codeOrId = searchParams.get('productCode') || searchParams.get('productId');
        if (!codeOrId || !props.model?.products) {
            return;
        }

        const product = fields.find(i => [i.unique_code, String(i.id)].includes(codeOrId));
        if (!product) {
            return;
        }

        if (product.id in collapsedRows) {
            setCollapsedRows(map => {
                delete map[product.id];

                return {...map};
            });
        }
    }, [collapsedRows, fields, props.model?.products, searchParams]);

    const tabName = props.tabName;

    useEffect(() => {
        if (tabsContext && tabName) {
            tabsContext.setBadge(tabName, {
                color: 'primary',
                label: String(fields.length)
            });
        }
    }, [tabsContext, tabName, fields.length]);

    const cannotEditCompletedProducts = cant('orders@can-edit-completed-products');
    const cannotIgnoreDeadlineExpirationReason = cant('can-ignore-asking-for-order-product-deadline-expiration-reason');

    const isDisabled = props.forceDisabled || designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly;
    const [listContentProps, formFieldArrayContextValues] = useMemo(() => {
        const fullModel = props.model;
        const orderId = fullModel?.id;
        const getProperty = (index: number, name: keyof IOrderProductModel, defaultValue = null) => {
            return fullModel?.products?.[index]?.[name] ?? defaultValue;
        };
        const isRowDisabled = (index: number) => (
                cannotEditCompletedProducts && getProperty(index, 'is_completed') === true ||
                getProperty(index, 'reason_of_deadline_expiration_is_required') === true &&
                cannotIgnoreDeadlineExpirationReason
        );

        const listContentProps: ControlledListProps<IOrderModel>['listContentProps'] = {
            tableCaptionNode: <TableCaption options={props.modelOptions}/>,
            tableHeadProps: {
                cellBeforeColumns: <th className="px-0" style={{width: '20px'}}/>,

                controlsTDProps: {style: {width: '90px'}},

                additionalControlNode: <button
                        type="button"
                        className="btn btn-sm btn-outline-success me-1"
                        disabled={isDisabled}
                        onClick={() => {
                            const uniqueCode = String(Date.now() + Math.random()).replace('.', '');

                            navigate({
                                ...location,
                                search: tap(new URLSearchParams(location.search), params => {
                                    params.set('productCode', uniqueCode);
                                }).toString(),
                            });

                            append({
                                unique_code: uniqueCode,
                                product_id: null,
                                department_id: null,
                                quantity: 1,
                                custom_price: null,
                                custom_price_type: 'for-pack',
                                deadline_at: null,
                            });
                        }}
                >
                    <i className="fa fa-plus"/>
                </button>,
            },
            tableBodyProps: {
                cellBeforeColumnsComponent: CellBeforeTableRestColumns,

                getTDProps(column): React.HTMLAttributes<HTMLTableDataCellElement> {
                    return columnsTDProps[column.name] ?? {};
                },

                renderRow(props, renderRow) {
                    const itemKey = props.model?.id ?? props.model?.autoGeneratedUuid;
                    const isExpanded = itemKey && !collapsedRows[itemKey];

                    return <>
                        <FormFieldDesignContextProvider contextValue={{
                            ...designContext,
                            fieldsAreDisabled: designContext.fieldsAreDisabled || isRowDisabled(props.rowIndex),
                        }}>
                            {renderRow()}
                        </FormFieldDesignContextProvider>

                        {(fullModel && props.model) ? renderRow(
                                {
                                    className: classnames('models-list__content-table__extra-row', {
                                        'd-none': !isExpanded
                                    })
                                },
                                () => <td colSpan={props.context?.columnsNumber}>
                                    <FormFieldDesignContextProvider>
                                        {(fullModel && props.model) ? <ExtraRowTabs
                                                orderId={orderId}
                                                model={fullModel}
                                                rowRecord={props.model}
                                                isDisabled={isDisabled || isRowDisabled(props.rowIndex)}
                                                rowIndex={props.rowIndex}
                                                context={props.context}
                                                rowId={props.model.id ?? props.model.autoGeneratedUuid}
                                                collapsedRows={collapsedRows}
                                                setCollapsedRows={setCollapsedRows}
                                        /> : null}
                                    </FormFieldDesignContextProvider>
                                </td>
                        ) : null}
                    </>;
                },

                renderCellContent(props, baseRenderer) {
                    if (props.config.name !== 'id') {
                        return baseRenderer();
                    }

                    return <IdField
                            id={props.model.id}
                            number={props.model.number_to_display}
                            uniqueCode={props.model.unique_code}
                            rowIndex={props.rowIndex}
                            itemKey={props.model.id ?? props.model.autoGeneratedUuid}
                            collapsedRows={collapsedRows}
                            setCollapsedRows={setCollapsedRows}
                    />;
                },

                renderControls(record, index) {
                    const isCompleted = (
                            record?.status?.is_completed === true ||
                            record?.status?.is_completed_without_payment === true
                    );
                    if (isRowDisabled(index) || isCompleted) {
                        return null;
                    }

                    const onClick = () => {
                        setCollapsedRows(map => {
                            const key = record?.id ?? record?.autoGeneratedUuid;
                            if (key in map) {
                                delete map[key];

                                return {...map};
                            }

                            return map;
                        });

                        remove(index);
                    };

                    if (record?.id) {
                        return <ConfirmableButton
                                size="sm"
                                color="danger"
                                disabled={isDisabled}
                                onClick={onClick}
                        >
                            <i className="fa fa-times"/>
                        </ConfirmableButton>;
                    }

                    return <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            disabled={isDisabled}
                            onClick={onClick}
                    >
                        <i className="fa fa-times"/>
                    </button>;
                }
            }
        };

        const formFieldArrayContextValues: IFormFieldItemPropsContext<IOrderModel> = {
            control,
            fieldName: props.fieldName,
            formName: props.formName,
            tabName: props.tabName,
            model: props.model,
            modelOptions: props.modelOptions,
            config: props.config,
            forceReadonly: props.config.readOnly === true || props.forceReadonly === true,
            forceDisabled: props.config.disabled === true || props.forceDisabled === true,
        };

        return [listContentProps, formFieldArrayContextValues];
    }, [
        props.model,
        props.modelOptions,
        props.fieldName,
        props.formName,
        props.tabName,
        props.config,
        props.forceReadonly,
        props.forceDisabled,
        isDisabled,
        control,
        cannotEditCompletedProducts,
        cannotIgnoreDeadlineExpirationReason,
        append,
        collapsedRows,
        designContext,
        remove,
        location,
        navigate,
    ]);

    return <FormFieldItemPropsContext.Provider value={formFieldArrayContextValues}>
        <ControlledList<IOrderModel>
                uniqueListName={`${props.formName}@${props.fieldName}--custom-list`}
                controllerName={props.formName}
                records={fields}
                columns={columns}
                withoutSearchField={true}
                keepInParentBox={true}
                listContentProps={listContentProps}
                listContextValues={listContextValues}
                bordered={true}
                className="mb-1"
        />
    </FormFieldItemPropsContext.Provider>;
}

export default function OrderProductsField<Model extends IOrderModel = IOrderModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
